"use client";

import { useMemo } from "react";
import { useParams, usePathname, useRouter } from "next/navigation";
import { localizePath, resolveCurrentLocale } from "@/utils/localize-path";

type AppRouter = ReturnType<typeof useRouter>;

export default function useLocalizedRouter() {
  const router = useRouter();
  const params = useParams();
  const pathname = usePathname();
  const locale = resolveCurrentLocale(params?.lang, pathname);

  return useMemo(() => {
    const localizeHref = (href: string) => localizePath(href, locale);

    return {
      push: (href: string, options?: Parameters<AppRouter["push"]>[1]) => {
        router.push(localizeHref(href), options);
      },
      replace: (href: string, options?: Parameters<AppRouter["replace"]>[1]) => {
        router.replace(localizeHref(href), options);
      },
      prefetch: (href: string, options?: Parameters<AppRouter["prefetch"]>[1]) => {
        router.prefetch(localizeHref(href), options);
      },
      back: () => router.back(),
      forward: () => router.forward(),
      refresh: () => router.refresh(),
      localizeHref,
    };
  }, [router, locale]);
}
